import { Col, Row, Select, Skeleton } from 'antd'
import React, { useEffect, useState } from 'react'
import EqButton from '../eqButton/EqButton'
import "./EqHorseStats.scss"
import moment, { Moment } from 'moment';
import Api from '../../shared/AApi';
import { ApiEndpoints } from '../../shared/Common';
import { useTranslation } from "react-i18next";
import { Empty } from 'antd-v5';
import AsyncImage from '../asyncImage/AsyncImage';
import EqCollapse from '../eqCollapse/EqCollapse';
import { HorseActivityStatsDto } from '../../shared/interfaces/horse/IHorses';



interface DayOfWeek {
    dayOfWeek: number;
    date: string;
    duration: number;
}

interface HorseActivityWeek {
    startOfWeek: Moment;
    endOfWeek: Moment;
    horseId: string;
    weekNumber: number;
    horseName: string;
    imageUrl?: string | null;
    daysOfWeek: DayOfWeek[];
}

interface SelectOption {
    key: string | number;
    value: string;
    weekNumber?: number;
    startOfWeek?: Moment;
    endOfWeek?: Moment;
}

interface ActivityFilterState {
    selectedHorse: string | null;
    selectedWeek: number | null;
    horseSelection: SelectOption[];
    weekSelection: SelectOption[];
}

interface HorseActivityData {
    allData: HorseActivityWeek[][];
    filteredData: HorseActivityWeek[][];
    ApiData: HorseActivityStatsDto | null;
}

const EqHorseStats = () => {
    const horseActivityDateFormat = "MM-DD-YYYY";
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const [check, setCheck] = useState(false);
    
    const initialFilterState: ActivityFilterState = {
        selectedHorse: null,
        selectedWeek: null,
        horseSelection: [],
        weekSelection: []
    };

    const [horsesActivityTimes, setHorsesActivityTimes] = useState<HorseActivityData>({
        allData: [],
        filteredData: [],
        ApiData: null
    });

    const [activityFilter, setActivityFilter] = useState<ActivityFilterState>(initialFilterState);
    const [isCollapseAll, setIsCollapseAll] = useState(false);

    const handleIsCollapseAll = (props: boolean) => {
        setIsCollapseAll(props);
    };

    const collapseAll = () => {
        handleIsCollapseAll(!isCollapseAll);
    };

    const handleHorseChange = (value: string) => {
        setActivityFilter({
            ...activityFilter, 
            selectedHorse: value
        });
        applyfilters(value, activityFilter.selectedWeek);
    };

    const handleWeekChange = (value: number) => {
        setActivityFilter({
            ...activityFilter, 
            selectedWeek: value
        });
        applyfilters(activityFilter.selectedHorse, value);
    };

    const applyfilters = (horseKey: string | null, weekKey: number | null, data: HorseActivityWeek[][] | null = null): HorseActivityWeek[][] => {
        const dataToFilter = data || horsesActivityTimes.allData;
        let filtered: HorseActivityWeek[][] = [];

        if (horseKey && weekKey) {
            filtered = dataToFilter.map(weekParam => 
                weekParam.filter(horseParam => 
                    horseParam.horseId === horseKey && horseParam.weekNumber === weekKey
                )
            );
        } else if (horseKey) {
            filtered = dataToFilter.map(weekParam => 
                weekParam.filter(horseParam => horseParam.horseId === horseKey)
            );
        } else if (weekKey) {
            filtered = dataToFilter.filter(weekParam => 
                weekParam.some(horseParam => horseParam.weekNumber === weekKey)
            );
        } else {
            filtered = [...dataToFilter];
        }

        setHorsesActivityTimes({ ...horsesActivityTimes, filteredData: filtered });
        return filtered;
    }

    const formatDuration = (minutes: number): string => {
        if (minutes) {
            if (minutes < 60) {
                return `${Math.floor(minutes % 60)}m`;
            }
            return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
        }
        return "-";
    }

    useEffect(() => {
        setLoading(true);
        Api.get<HorseActivityStatsDto>(ApiEndpoints.horse.getAllHorseActivityStatsDto).then(
            (response) => {
                sortHorseActivityData(response.data);
                setLoading(false);
            }, 
            (error) => {
                console.log(error);
                setLoading(false);
            }
        );
    }, []);

    const sortHorseActivityData = (data: HorseActivityStatsDto) => {
        const weekSelectData: SelectOption[] = [];
        const horseSelectData: SelectOption[] = [];
        

        // Combine EventUserGroupActivities and HorseTrainings
        const combinedActivities = [...data.eventUserGroupActivities, ...data.horseTrainings];
        
        // const combinedActivities = data?.flatMap(horse => [
        //     ...horse.eventUserGroupActivities,
        //     ...horse.horseTrainings
        // ]);


        const groups = combinedActivities.reduce((groups: Record<number, HorseActivityWeek[]>, activity) => {
            const startTime = moment(activity.startTime);
            const startOfWeek = startTime.clone().startOf('isoWeek');
            const endOfWeek = startTime.clone().endOf('isoWeek');
            const date = startTime.format(horseActivityDateFormat);
            const weekNumber = startTime.isoWeek();
            const yearNumber = startTime.year();

            if (!groups[weekNumber]) {
                groups[weekNumber] = [];
                weekSelectData.push({
                    key: weekNumber,
                    value: `${t('week-')}-${weekNumber}`,
                    weekNumber,
                    startOfWeek,
                    endOfWeek,
                });
            }

            let existingHorse = groups[weekNumber].find(h => h.horseId === activity.horseId);
            if (!existingHorse) {
                if (!horseSelectData.some(h => h.key === activity.horseId)) {
                    horseSelectData.push({
                        key: activity.horseId || '',
                        value: activity.horseName
                    });
                }

                existingHorse = {
                    startOfWeek,
                    endOfWeek,
                    horseId: activity.horseId || '',
                    weekNumber,
                    horseName: activity.horseName,
                    imageUrl: activity.horseImageUrl,
                    daysOfWeek: daysOfWeek(yearNumber, weekNumber)
                };
                groups[weekNumber].push(existingHorse);
            }

            const day = existingHorse.daysOfWeek.find(d => d.date === date);
            if (day) {
                day.duration += activity.duration;
            }

            return groups;
        }, {});

        weekSelectData.sort((a, b) => a.weekNumber! - b.weekNumber!);

        const groupArrays = Object.values(groups);
        const selectedHorse = horseSelectData[0]?.key as string || null;
        const selectedWeek = weekSelectData[0]?.key as number || null;

        setActivityFilter({
            ...activityFilter,
            weekSelection: weekSelectData,
            horseSelection: horseSelectData,
            selectedWeek
        });

        const filtered = applyfilters(null, selectedWeek, groupArrays);
        setHorsesActivityTimes({ 
            filteredData: filtered, 
            allData: groupArrays, 
            ApiData: data 
        });
    }

    const daysOfWeek = (yearNumber: number, weeknumber: number): DayOfWeek[] => {
        return [1, 2, 3, 4, 5, 6, 7].map(d => ({
            dayOfWeek: d,
            date: moment(`${yearNumber}-${weeknumber}-${d}`, 'YYYY-W-E').format(horseActivityDateFormat),
            duration: 0
        }));
    }

    const getWeekDay = (index: number): string => {
        switch (index) {
            case 1: return t('monday');
            case 2: return t('tuesday');
            case 3: return t('wednesday');
            case 4: return t('thursday');
            case 5: return t('friday');
            case 6: return t('saturday');
            case 7: return t('sunday');
            default: return t('day');
        }
    }

    const formatTotal = (daysOfWeek: DayOfWeek[]): string => {
        const totalTime = daysOfWeek.reduce((sum, day) => sum + day.duration, 0);
        return formatDuration(totalTime);
    }

    return (
        <div className="horse-management">
            {/* Filter section */}
            <Row>
                <Col span={24} className="filters">
                    <Row gutter={[16, 16]} className="filter-type">
                        <Col className="location-col" xs={24} sm={24} md={12} lg={11}>
                            <Row className="location-selector">
                                <Col xs={6} sm={5} md={6} lg={5}>
                                    <label className="picker-label">{t("horse")}</label>
                                </Col>
                                <Col className="location-input" flex="auto">
                                    <Select
                                        className="eq-filter-radius eq-input-info"
                                        allowClear
                                        placeholder={t("selectHorse")}
                                        onChange={handleHorseChange}
                                        value={activityFilter.selectedHorse}
                                    >
                                        {activityFilter.horseSelection.map(option => (
                                            <Select.Option
                                                key={option.key}
                                                value={option.key}
                                            >
                                                <span>{option.value}</span>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Col>
                            </Row>
                        </Col>
                        <Col className="location-col" xs={24} sm={24} md={12} lg={11}>
                            <Row className="location-selector">
                                <Col xs={6} sm={5} md={6} lg={5}>
                                    <label className="picker-label">{t("week")}</label>
                                </Col>
                                <Col className="location-input" flex="auto">
                                    <Select
                                        className="eq-filter-radius eq-input-info"
                                        allowClear={false}
                                        onChange={handleWeekChange}
                                        value={activityFilter.selectedWeek}
                                    >
                                        {activityFilter.weekSelection.map(option => (
                                            <Select.Option
                                                key={option.key}
                                                value={option.key}
                                            >
                                                <span className='week-number'>{t("week")}--{option.weekNumber}</span>
                                                <span className='week-limits'>
                                                    {option.startOfWeek?.format(horseActivityDateFormat)} -
                                                    {option.endOfWeek?.format(horseActivityDateFormat)}
                                                </span>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Col>
            </Row>

            <div className="loading"><Skeleton loading={loading} active></Skeleton></div>
            
            {!loading && horsesActivityTimes.filteredData.length ? (
                <Col span={24}>
                    {/* Table header */}
                    <Row className="eq-fl-row hidden-sm" justify="space-between">
                        <Col className="eq-fl-th" span={5}>{t("name")}</Col>
                        <Col className="eq-fl-th" span={2}>{t("monday")}</Col>
                        <Col className="eq-fl-th" span={2}>{t("tuesday")}</Col>
                        <Col className="eq-fl-th" span={2}>{t("wednesday")}</Col>
                        <Col className="eq-fl-th" span={2}>{t("thursday")}</Col>
                        <Col className="eq-fl-th" span={2}>{t("friday")}</Col>
                        <Col className="eq-fl-th" span={2}>{t("saturday")}</Col>
                        <Col className="eq-fl-th" span={2}>{t("sunday")}</Col>
                        <Col className="eq-fl-th" span={2}>{t("total")}</Col>
                    </Row>

                    <div className="hidden-md">
                        <Row justify="start" align="middle" className="eq-list-checkAll">
                            <Col>
                                <EqButton
                                    classNames="btn-background-orange"
                                    onClick={collapseAll}
                                >
                                    {isCollapseAll ? "Collapse All" : "Expand All"}
                                </EqButton>
                            </Col>
                        </Row>
                    </div>

                    <div className="horse-task-list">
                        {horsesActivityTimes.filteredData.map((weekGroup, index) => (
                            <React.Fragment key={index}>
                                {weekGroup.map(horse => (
                                    <>
                                        {/* Desktop view */}
                                        <Row className="eq-f-row hidden-sm" justify="space-between" key={horse.horseId}>
                                            <Col className="eq-f-td horse-task-detail" span={5}>
                                                <div className="task-info">
                                                    <AsyncImage
                                                        className="event-img"
                                                        alt="example"
                                                        src={horse.imageUrl as string}
                                                    />
                                                </div>
                                                <div>{horse.horseName}</div>
                                            </Col>
                                            {horse.daysOfWeek.map(day => (
                                                <Col className="eq-f-td" span={2} key={day.dayOfWeek}>
                                                    {formatDuration(day.duration)}
                                                </Col>
                                            ))}
                                            <Col className="eq-f-td" span={2}>
                                                {formatTotal(horse.daysOfWeek)}
                                            </Col>
                                        </Row>

                                        {/* Mobile view */}
                                        <div className="hidden-md" key={`mobile-${horse.horseId}`}>
                                            <EqCollapse header={
                                                <Row justify="start" align="middle">
                                                    <Col flex="auto">{horse.horseName}</Col>
                                                </Row>
                                            }>
                                                <Row className="eq-card-row">
                                                    <Col className="eq-card-col-heading" span={24}>
                                                        {t("name")}
                                                    </Col>
                                                    <Col className="eq-card-row horse-task-detail" span={24}>
                                                        <div className="task-info">
                                                            <AsyncImage
                                                                className="event-img"
                                                                alt="example"
                                                                src={horse.imageUrl as string}
                                                            />
                                                        </div>
                                                        <div>{horse.horseName}</div>
                                                    </Col>
                                                </Row>
                                                {horse.daysOfWeek.map((day, dayIndex) => (
                                                    <React.Fragment key={dayIndex}>
                                                        <Row className="eq-card-row">
                                                            <Col className="eq-card-col-heading" span={24}>
                                                                {getWeekDay(day.dayOfWeek)}
                                                            </Col>
                                                            <Col className="eq-card-row horse-task-detail" span={24}>
                                                                <div>{formatDuration(day.duration)}</div>
                                                            </Col>
                                                        </Row>
                                                        <Row>
                                                            <Col flex="auto" className="eq-card-divider"></Col>
                                                        </Row>
                                                    </React.Fragment>
                                                ))}
                                            </EqCollapse>
                                        </div>
                                    </>
                                ))}
                            </React.Fragment>
                        ))}
                    </div>
                </Col>
            ) : !loading && (
                <div className="no-data">
                    <Empty description={t("noHorseStatsAvailable")} />
                </div>
            )}
        </div>
    );
};

export default EqHorseStats;